import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Divider,
    Flex,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Text,
    Textarea,
    Tooltip,
} from '@chakra-ui/react'
import { Icon } from '@chakra-ui/icons'
import { FaWandMagicSparkles, FaRegLightbulb } from 'react-icons/fa6'

import { CallFieldsFragment, ClinicFieldsFragment } from '~graphql/generated/graphql'
import OutlineBox from '~components/shared/wrappers/outline-box'
import SharedStack from '~components/shared/wrappers/shared-stack'
import SharedSectionHeader from '~components/shared/texts/shared-section-header'
import { useCallsContext } from '../provider'
import ScoreBadge, {
    MAX_CALL_SCORE,
    MIN_CALL_SCORE,
    getScoreColorScheme,
    getScoreLabel,
} from '../score-badge'
import SentimentBadge from '../sentiment-badge'
import { useTeachAgent } from '../use-teach-agent'

interface Props {
    isOpen: boolean
    onClose: () => void
    call: CallFieldsFragment | null
    /**
     * Clinic powering the Teach-the-Agent section. Defaults to the
     * CallsProvider clinic; pass explicitly when rendering outside the calls
     * table (e.g. from chat). When absent, the section is hidden.
     */
    clinic?: ClinicFieldsFragment | null
}

export default function CallDetailModal({
    isOpen,
    onClose,
    call,
    clinic: clinicProp,
}: Props) {
    const { clinic: contextClinic } = useCallsContext()
    const clinic = clinicProp ?? contextClinic

    if (!call) return null

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="2xl"
            scrollBehavior="inside"
        >
            <ModalOverlay />
            <ModalContent maxH="85vh">
                <ModalHeader>
                    <SharedStack spacing={1}>
                        <Text fontSize="lg" fontWeight="bold">
                            Call Details
                        </Text>
                        {call.transcription_summary && (
                            <Text
                                fontSize="sm"
                                color="gray.500"
                                fontWeight="normal"
                            >
                                {call.transcription_summary}
                            </Text>
                        )}
                    </SharedStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <SharedStack spacing={5}>
                        <ScoreSection call={call} />
                        {call.is_automated && clinic && (
                            <TeachAgentSection
                                call={call}
                                clinic={clinic}
                                onSavedAndClose={onClose}
                            />
                        )}
                        {call.notes && (
                            <Box>
                                <SharedSectionHeader>
                                    Notes
                                </SharedSectionHeader>
                                <Text
                                    fontSize="sm"
                                    color="gray.600"
                                    whiteSpace="pre-wrap"
                                    mt={1}
                                >
                                    {call.notes}
                                </Text>
                            </Box>
                        )}
                        <TranscriptSection call={call} />
                    </SharedStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

// ============================================================================
// Score
// ============================================================================

function ScoreSection({ call }: { call: CallFieldsFragment }) {
    const { score, score_reasoning } = call
    const colorScheme = getScoreColorScheme(score)

    return (
        <OutlineBox
            borderColor={`${colorScheme}.200`}
            bg={`${colorScheme}.50`}
            px={5}
            py={4}
        >
            <SharedStack spacing={3}>
                <HStack spacing={3} align="center" flexWrap="wrap">
                    <SharedSectionHeader>
                        {call.is_automated ? 'AI QA Score' : 'Call QA Score'}
                    </SharedSectionHeader>
                    <ScoreBadge
                        score={score}
                        showDenominator
                        fontSize="sm"
                        px={2.5}
                        py={1}
                    />
                    <Text fontSize="xs" color="gray.600">
                        {getScoreLabel(score)}
                    </Text>
                    <SentimentBadge
                        sentiment={call.sentiment_customer}
                        agentSentiment={call.sentiment_agent}
                        fontSize="sm"
                        px={2.5}
                        py={1}
                    />
                </HStack>
                {score == null ? (
                    <Text fontSize="sm" color="gray.500">
                        This call hasn't been scored yet. Scoring runs
                        automatically once the call ends and a transcript is
                        available.
                    </Text>
                ) : score_reasoning ? (
                    <Text fontSize="sm" color="gray.700" lineHeight="1.7">
                        {score_reasoning}
                    </Text>
                ) : (
                    <Text fontSize="sm" color="gray.500" fontStyle="italic">
                        No reasoning was recorded for this score.
                    </Text>
                )}
                {score != null && call.is_automated && (
                    <Text fontSize="xs" color="gray.500">
                        {`Scored on a ${MIN_CALL_SCORE}-${MAX_CALL_SCORE} scale — most calls land in the 6-8 range.`}
                    </Text>
                )}
            </SharedStack>
        </OutlineBox>
    )
}

// ============================================================================
// Teach the Agent
// ============================================================================

function TeachAgentSection({
    call,
    clinic,
    onSavedAndClose,
}: {
    call: CallFieldsFragment
    clinic: ClinicFieldsFragment
    onSavedAndClose: () => void
}) {
    const [draft, setDraft] = useState('')
    const [shouldClose, setShouldClose] = useState(false)

    // Reset draft whenever a different call is opened.
    useEffect(() => {
        setDraft('')
        setShouldClose(false)
    }, [call.id])

    const { addInstruction, isSaving, currentInstructions } = useTeachAgent({
        clinic,
        onSuccess: () => {
            setDraft('')
            if (shouldClose) onSavedAndClose()
            setShouldClose(false)
        },
    })

    const trimmed = draft.trim()
    const canSave = trimmed.length > 0 && !isSaving

    function handleSave(closeAfter: boolean) {
        if (!canSave) return
        setShouldClose(closeAfter)
        addInstruction(trimmed)
    }

    return (
        <OutlineBox
            borderColor="brand.200"
            bg="brand.50"
            px={5}
            py={4}
        >
            <SharedStack spacing={3}>
                <HStack spacing={2} align="center">
                    <Icon as={FaWandMagicSparkles} color="brand.500" />
                    <SharedSectionHeader>Teach the Agent</SharedSectionHeader>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                    Add a guideline based on this call. It&apos;s added to your
                    AI Receptionist&apos;s instructions and used on future
                    calls.
                </Text>
                <Textarea
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    placeholder='e.g. "When a caller asks about boarding, always offer to book a tour first."'
                    rounded="lg"
                    bg="white"
                    rows={3}
                    resize="vertical"
                    fontSize="sm"
                />
                <Flex
                    align="center"
                    justify="space-between"
                    flexWrap="wrap"
                    gap={2}
                >
                    <Tooltip
                        label={`${currentInstructions.length} active instruction${currentInstructions.length === 1 ? '' : 's'} on your AI Receptionist`}
                        hasArrow
                        placement="top"
                    >
                        <HStack spacing={1.5} color="gray.500">
                            <Icon as={FaRegLightbulb} boxSize="14px" />
                            <Text fontSize="xs">
                                {currentInstructions.length} active instruction
                                {currentInstructions.length === 1 ? '' : 's'}
                            </Text>
                        </HStack>
                    </Tooltip>
                    <HStack spacing={2}>
                        <Button
                            size="sm"
                            variant="ghost"
                            colorScheme="brand"
                            onClick={() => handleSave(false)}
                            isDisabled={!canSave}
                            isLoading={isSaving && !shouldClose}
                        >
                            Add Instruction
                        </Button>
                        <Button
                            size="sm"
                            colorScheme="brand"
                            onClick={() => handleSave(true)}
                            isDisabled={!canSave}
                            isLoading={isSaving && shouldClose}
                        >
                            Add &amp; Close
                        </Button>
                    </HStack>
                </Flex>
            </SharedStack>
        </OutlineBox>
    )
}

// ============================================================================
// Transcript
// ============================================================================

function TranscriptSection({ call }: { call: CallFieldsFragment }) {
    const hasTranscript = !!call.transcription_full_text

    return (
        <Box>
            <Divider mb={3} />
            <SharedStack spacing={2}>
                <SharedSectionHeader>Transcript</SharedSectionHeader>
                {hasTranscript ? (
                    <Box
                        whiteSpace="pre-wrap"
                        fontSize="sm"
                        color="gray.700"
                        lineHeight="1.7"
                        maxH="40vh"
                        overflowY="auto"
                        bg="gray.50"
                        rounded="lg"
                        p={4}
                        borderWidth="1px"
                        borderColor="gray.200"
                    >
                        {call.transcription_full_text}
                    </Box>
                ) : (
                    <Text color="gray.500" fontSize="sm">
                        No transcript available for this call.
                    </Text>
                )}
            </SharedStack>
        </Box>
    )
}
